import { ElementRef, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { AnimatePresence, motion } from 'motion/react';
import { theme } from '@frontend/theme';
import { NakedButton, styles as dsStyles } from '@frontend/design-system';
import { getMinDate } from '../../../utils/date-helpers';
import { OVERLAPPING_EVENTS_BUTTON_WIDTH } from '../constants';
import { useCalendarView, useCalendarViewProps } from '../providers';
import { ProcessedEventData } from '../types';
import { createGridColumnsFromStartAndEndTimestamp, PX_PER_MINUTE, zIndex } from '../utils';

type OverlappingEventsProps = {
  overlappingEvents: ProcessedEventData[];
  containerRef: React.RefObject<ElementRef<'article'>>;
  locationName: string;
  visibleLaneCount: number;
};

export const OverlappingEvents = ({ visibleLaneCount, overlappingEvents, locationName }: OverlappingEventsProps) => {
  const [visible, setVisible] = useState(false);
  const { setEventCarouselData } = useCalendarView(['setEventCarouselData']);
  const { startHour: calendarStartHour, endHour: calendarEndHour } = useCalendarViewProps();

  const hiddenEvents = useMemo(
    () => overlappingEvents.filter((event) => event.laneIndex + 1 > visibleLaneCount),
    [overlappingEvents, visibleLaneCount]
  );

  const minStartTime = useMemo(
    () => getMinDate(hiddenEvents.map((event) => dayjs(event.startHour, 'hh:mm A'))),
    [hiddenEvents]
  );

  useEffect(() => {
    setVisible(!!hiddenEvents.length);
  }, [hiddenEvents]);

  if (!minStartTime || !minStartTime.isValid()) return null;

  const { startGridColumn, eventDuration } = createGridColumnsFromStartAndEndTimestamp(
    calendarStartHour,
    calendarEndHour
  )({
    startHourTimestamp: minStartTime.format('hh:mm A'),
    endHourTimeStamp: dayjs(minStartTime).add(30, 'm').format('hh:mm A'),
  });

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0, zIndex: zIndex.eventCardDefault }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          css={styles.buttonContainer(startGridColumn, eventDuration * PX_PER_MINUTE)}
        >
          <NakedButton
            onClick={(e) => {
              e.stopPropagation();
              setEventCarouselData({
                events: hiddenEvents,
                locationName,
                isOpen: true,
              });
            }}
            onMouseDown={(e) => e.stopPropagation()}
            css={[dsStyles.flexCenter, styles.button]}
          >
            +{hiddenEvents.length}
          </NakedButton>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const styles = {
  buttonContainer: (top: number, height: number) =>
    css({
      top,
      height,
      right: 0,
      position: 'absolute',
      zIndex: zIndex.overlappingEventButton,
      ':hover': {
        zIndex: zIndex.eventCardDefault,
      },
    }),
  button: css({
    width: OVERLAPPING_EVENTS_BUTTON_WIDTH,
    height: 40,
    color: theme.colors.neutral90,
    backgroundColor: theme.colors.white,
    borderRadius: 500,
    fontSize: theme.font.size.medium,
    fontWeight: theme.font.weight.bold,
    userSelect: 'none',
    cursor: 'pointer',
    boxShadow: theme.shadows.light,
    transition: 'box-shadow 0.2s ease-in-out, scale 0.2s ease-in-out',
    ':hover': {
      scale: 1.1,
      boxShadow: theme.shadows.floating,
    },
  }),
};
